// Analytics module — supports Google Analytics 4 and PostHog
// Configure via environment variables:
//   VITE_GA_MEASUREMENT_ID — for GA4 (e.g., G-XXXXXXX)
//   VITE_POSTHOG_KEY — for PostHog
//   VITE_POSTHOG_HOST — PostHog host (defaults to https://us.i.posthog.com)

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const PH_KEY = import.meta.env.VITE_POSTHOG_KEY;
const PH_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let gaInitialized = false;
let phInitialized = false;

/** Initialize GA4 — loads the gtag script dynamically */
function initGA() {
  if (gaInitialized || !GA_ID) return;
  gaInitialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
}

/** Initialize PostHog */
function initPostHog() {
  if (phInitialized || !PH_KEY) return;
  phInitialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `${PH_HOST}/static/array.js`;
  script.onload = () => {
    window.posthog?.init(PH_KEY, {
      api_host: PH_HOST,
      capture_pageview: false,
      capture_pageleave: true,
    });
  };
  document.head.appendChild(script);
}

/** Initialize all configured analytics providers */
export function initAnalytics() {
  if (typeof window === 'undefined') return;
  initGA();
  initPostHog();
}

/** Track a page view */
export function trackPageView(path?: string) {
  const pagePath = path || window.location.pathname + window.location.search;

  if (GA_ID && window.gtag) {
    window.gtag('event', 'page_view', { page_path: pagePath });
  }

  if (PH_KEY && window.posthog) {
    window.posthog.capture('$pageview', { $current_url: pagePath });
  }
}

/** Track a custom event */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (GA_ID && window.gtag) {
    window.gtag('event', eventName, properties);
  }

  if (PH_KEY && window.posthog) {
    window.posthog.capture(eventName, properties);
  }
}

/**
 * Track sign_up exactly once per user per browser. Signup can be observed
 * from two places (the email signUp action and the OAuth get-or-create path
 * in initialize), and initialize re-runs on every app load — the localStorage
 * flag keeps the funnel event from double-counting.
 */
export function trackSignUpOnce(userId: string, method: string) {
  try {
    const key = `pm_signup_tracked:${userId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // storage unavailable (private mode) — still send the event
  }
  trackEvent('sign_up', { method });
}

/** Track daily_goal_met once per UTC day (stats reload on every answer). */
export function trackDailyGoalMetOnce(dateUTC: string) {
  try {
    const key = 'pm_goal_met_tracked';
    if (localStorage.getItem(key) === dateUTC) return;
    localStorage.setItem(key, dateUTC);
  } catch {
    return; // without storage we can't dedupe a high-frequency path; skip
  }
  trackEvent('daily_goal_met');
}

/** Identify a user (call after signup/login) */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (GA_ID && window.gtag) {
    window.gtag('set', 'user_properties', { user_id: userId, ...traits });
  }

  if (PH_KEY && window.posthog) {
    window.posthog.identify(userId, traits);
  }
}

// Type declarations for window globals
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    posthog?: {
      init: (key: string, config: Record<string, unknown>) => void;
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (id: string, traits?: Record<string, unknown>) => void;
    };
  }
}
